import { BrowserRouter, Routes, Route } from 'react-router'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import * as Sentry from '@sentry/react'
import { AuthKitProvider } from '@workos-inc/authkit-react'
import { AppShell } from './components/layout/AppShell'
import { HomePage } from './pages/HomePage'
import { DashboardPage } from './pages/DashboardPage'
import { PhonePage } from './pages/PhonePage'
import { CallDetailPage } from './pages/CallDetailPage'
import { VapiVoiceTestPage } from './pages/VapiVoiceTestPage'
import { ContactsPage } from './pages/ContactsPage'
import { FollowUpsPage } from './pages/FollowUpsPage'
import { FollowUpDetailPage } from './pages/FollowUpDetailPage'
import { DirectoryPage } from './pages/DirectoryPage'
import { ContactDetailPage } from './pages/ContactDetailPage'
import { AgentsPage } from './pages/AgentsPage'
import { AgentDetailPage } from './pages/AgentDetailPage'
import { CallStagingPage } from './pages/CallStagingPage'
import { AgentCallPage } from './pages/AgentCallPage'
import { CopilotCallPage } from './pages/CopilotCallPage'
import { CopilotSandboxPage } from './pages/CopilotSandboxPage'
import { CallHistoryPage } from './pages/CallHistoryPage'
import { CampaignListsPage } from './pages/CampaignListsPage'
import { CampaignDetailPage } from './pages/CampaignDetailPage'
import { CampaignRunnerPage } from './pages/CampaignRunnerPage'
import { SimulationCampaignsPage } from './pages/SimulationCampaignsPage'
import { SimulationCampaignDetailPage } from './pages/SimulationCampaignDetailPage'
import { SimulationRunnerPage } from './pages/SimulationRunnerPage'
import { OrganizationsPage } from './pages/OrganizationsPage'
import { MembersPage } from './pages/MembersPage'
import { ApiTokensPage } from './pages/ApiTokensPage'
import { UsagePage } from './pages/UsagePage'
import { AuthorizeAppPage } from './pages/AuthorizeAppPage'
import { OnboardingPage } from './pages/OnboardingPage'
import { RequestAccessPage } from './pages/RequestAccessPage'
import { ProfilePage } from './pages/ProfilePage'
import { AcceptInvitePage } from './pages/AcceptInvitePage'
import { AdminBillingPage } from './pages/AdminBillingPage'
import { PilotLeadsPage } from './pages/admin/PilotLeadsPage'
import { BillingReturnPage } from './pages/BillingReturnPage'
import { BillingCanceledPage } from './pages/BillingCanceledPage'
import { SuperadminGate } from './components/auth/SuperadminGate'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 10_000,
    },
  },
})

function ErrorFallback() {
  return (
    <div style={{ padding: '2rem', fontFamily: 'system-ui, sans-serif', textAlign: 'center' }}>
      <h1 style={{ fontSize: '1.25rem', marginBottom: '0.5rem' }}>Something went wrong.</h1>
      <p style={{ color: '#666' }}>Refresh to retry.</p>
    </div>
  )
}

export default function App() {
  const clientId = import.meta.env.VITE_WORKOS_CLIENT_ID ?? ''
  const apiHostname = import.meta.env.VITE_WORKOS_API_HOSTNAME ?? undefined
  const devMode = (import.meta.env.VITE_WORKOS_DEV_MODE ?? '').toString() === 'true'
  return (
    <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
    <AuthKitProvider clientId={clientId} apiHostname={apiHostname} devMode={devMode}>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          {/* Public marketing site. No AppShell, no WorkOS gate. Logged-in
              visits self-redirect to /app from inside the page. */}
          <Route index element={<HomePage />} />

          {/* OAuth consent page — standalone, no AppShell chrome. */}
          <Route path="authorize" element={<AuthorizeAppPage />} />
          {/* Onboarding & invite-acceptance — standalone, the user has no
              org yet so the AppShell sidebar/org-switcher would be empty. */}
          <Route path="onboarding" element={<OnboardingPage />} />
          <Route path="request-access" element={<RequestAccessPage />} />
          <Route path="invites/:token" element={<AcceptInvitePage />} />
          {/* Layer 2 Stripe Checkout return-page + cancellation. Standalone
              because they may render before subscription state has updated
              and we don't want the sidebar showing a stale gate state. */}
          <Route path="billing/return" element={<BillingReturnPage />} />
          <Route path="billing/canceled" element={<BillingCanceledPage />} />

          {/* Authenticated app — lives under /app. */}
          <Route path="app" element={<AppShell />}>
            <Route index element={<DashboardPage />} />
            <Route path="phone/:phoneNumber" element={<PhonePage />} />
            <Route path="calls" element={<CallHistoryPage />} />
            <Route path="calls/stage/:phoneNumber?" element={<CallStagingPage />} />
            <Route path="calls/agent/:phoneNumber?" element={<AgentCallPage />} />
            <Route path="calls/copilot/sandbox" element={<CopilotSandboxPage />} />
            <Route path="calls/copilot/:phoneNumber?" element={<CopilotCallPage />} />
            <Route path="campaigns" element={<CampaignListsPage />} />
            <Route path="campaigns/:id" element={<CampaignDetailPage />} />
            <Route path="campaigns/:id/run" element={<CampaignRunnerPage />} />
            <Route path="calls/:callSid" element={<CallDetailPage />} />
            <Route path="voice-test" element={<VapiVoiceTestPage />} />
            <Route path="contacts" element={<ContactsPage />} />
            <Route path="contacts/:contactId" element={<ContactDetailPage />} />
            <Route path="agents" element={<AgentsPage />} />
            <Route path="agents/:agentId" element={<AgentDetailPage />} />
            <Route path="follow-ups" element={<FollowUpsPage />} />
            <Route path="follow-ups/:followUpId" element={<FollowUpDetailPage />} />
            <Route path="directory" element={<DirectoryPage />} />
            <Route path="organizations" element={<OrganizationsPage />} />
            <Route path="members" element={<MembersPage />} />
            <Route path="tokens" element={<ApiTokensPage />} />
            <Route path="usage" element={<UsagePage />} />
            <Route path="profile" element={<ProfilePage />} />
            {/* Superadmin-only — non-superadmins get redirected to /app. */}
            <Route element={<SuperadminGate />}>
              <Route path="simulations" element={<SimulationCampaignsPage />} />
              <Route path="simulations/:id" element={<SimulationCampaignDetailPage />} />
              <Route path="simulations/:id/run" element={<SimulationRunnerPage />} />
              <Route path="admin" element={<AdminBillingPage />} />
              <Route path="pilot-leads" element={<PilotLeadsPage />} />
            </Route>
          </Route>
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
    </AuthKitProvider>
    </Sentry.ErrorBoundary>
  )
}
